/* Peetal Parampara loader (16 Oct 2026). Loaded with defer from snippets/pp-loader-nav.liquid.
   Part 1, page change: a same-origin link or GET form that has not painted the next page after
   300 ms shows a 2px gold line at the top. Never blocks, never covers. Hidden on pageshow (bfcache).
   Part 2, busy states: Horizon has no spinner, so this watches fetch(). When a fetch starts within
   2.5 s of a tap, key press or typing, the element that waits gets data-pp-busy and the diya mark.
   CSS in assets/pp-loader.css. The mark comes from <template id="pp-loader-tpl">. */
(() => {
  /* Part 1: page change line */
  let line, timer;
  const hide = () => {
    clearTimeout(timer);
    if (line) line.classList.remove('is-on');
  };
  const go = (url) => {
    const u = new URL(url, location.href);
    if (u.origin !== location.origin || (u.pathname === location.pathname && u.search === location.search)) return;
    hide();
    timer = setTimeout(() => {
      if (!line) {
        line = document.createElement('div');
        line.className = 'pp-navline';
        line.setAttribute('aria-hidden', 'true');
        document.body.append(line);
      }
      line.classList.add('is-on');
    }, 300);
  };
  addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if ((a.target && a.target !== '_self') || a.hasAttribute('download')) return;
    go(a.href);
  });
  addEventListener('submit', (e) => {
    const f = e.target;
    if (!e.defaultPrevented && f.method === 'get' && (!f.target || f.target === '_self')) go(f.action);
  });
  addEventListener('pageshow', hide);
  addEventListener('pagehide', hide);

  /* Part 2: busy states */
  const tpl = document.getElementById('pp-loader-tpl');
  if (!tpl || !window.fetch) return;
  let src = null;
  let at = 0;
  const mark = (e) => {
    src = e.target;
    at = performance.now();
  };
  addEventListener('pointerdown', mark, true);
  addEventListener('input', mark, true);
  addEventListener('change', mark, true);
  addEventListener('keydown', (e) => (e.key === 'Enter' || e.key === ' ') && mark(e), true);

  /* Pick the element that waits for this request, from where the shopper acted. */
  const pick = (url) => {
    if (!(src instanceof Element) || performance.now() - at > 2500) return null;
    const s = src;
    const btn = s.closest('button');
    if (/\/cart\/add/.test(url)) return btn && [btn, 'i', 1];
    if (/\/cart\/(change|update)/.test(url)) {
      const row = s.closest('tr');
      const rm = row && row.querySelector('.cart-items__remove');
      return rm && [rm, 'i'];
    }
    const ps = s.closest('predictive-search-component');
    if (ps) {
      const icon = ps.querySelector('.predictive-search__icon');
      return icon && s.matches('input') && [icon, 'i'];
    }
    if (s.closest('variant-picker')) {
      const box = s.closest('.product-details, dialog, section') || document;
      const atc = box.querySelector('.add-to-cart-button');
      return atc && [atc, 'i'];
    }
    if (s.closest('quick-add-component') && btn && /\/products\//.test(url)) return [btn, 'i'];
    return null;
  };

  const busy = new Map();
  const start = (el, kind, now) => {
    const n = (busy.get(el) || 0) + 1;
    busy.set(el, n);
    if (n > 1) return;
    el.setAttribute('data-pp-busy', kind + (now ? 'n' : ''));
    el.setAttribute('aria-busy', 'true');
    const ld = tpl.content.firstElementChild.cloneNode(true);
    if (kind === 'b') {
      const plate = document.createElement('span');
      plate.className = 'pp-ld-plate' + (now ? ' is-now' : '');
      plate.append(ld);
      document.body.append(plate);
      el._ppLd = plate;
    } else {
      /* A button with bare text (no inner element) hides its text by colour; the mark keeps the old colour. */
      if ([...el.childNodes].some((c) => c.nodeType === 3 && c.textContent.trim())) {
        el.style.setProperty('--pp-ld-c', getComputedStyle(el).color);
        el.setAttribute('data-pp-busy', 't');
      }
      /* The box must hold the mark. Only a static box gets position: relative, and only while busy. */
      if (getComputedStyle(el).position === 'static') el.style.position = 'relative', (el._ppPos = 1);
      el.append(ld);
      el._ppLd = ld;
    }
  };
  const stop = (el) => {
    const n = (busy.get(el) || 1) - 1;
    if (n > 0) return busy.set(el, n);
    busy.delete(el);
    el.removeAttribute('data-pp-busy');
    el.removeAttribute('aria-busy');
    if (el._ppLd) el._ppLd.remove();
    if (el._ppPos) el.style.position = '';
    el.style.removeProperty('--pp-ld-c');
    el._ppLd = el._ppPos = null;
  };
  /* The theme shows its "Added" tick at the tap and clears it about 800 ms later, often before the
     answer. Once the answer is in, (re)start the theme's own tick so it stays up after the mark. */
  const added = (btn) => {
    const c = btn.closest('add-to-cart-component');
    if (c && c.animateAddToCart) return c.animateAddToCart();
    clearTimeout(btn._ppTick);
    btn.dataset.added = 'true';
    btn._ppTick = setTimeout(() => btn.removeAttribute('data-added'), 1500);
  };

  /* Filters. On desktop the theme swaps the grid inside a view transition, which freezes the screen
     until the new grid is ready, and it may answer from a hover prefetch. So start on the theme's own
     filter:update event, before the freeze, with the plate shown at once (it stays still while frozen).
     Skip it when this exact grid was fetched before (the theme answers from its cache). Stop when the
     section fetch for the new URL is read. */
  const keyOf = (url) => {
    const x = new URL(url, location.href);
    x.searchParams.delete('section_id');
    x.searchParams.sort();
    return x.pathname + x.search;
  };
  const seen = new Set();
  const begun = new Set();
  const waits = new Map();
  document.addEventListener('filter:update', (e) => {
    /* In the phone filter drawer the grid is hidden behind the drawer: the mark goes in "See N items". */
    const dlg = e.target.closest('dialog');
    const list = dlg ? dlg.querySelector('.facets__see-results') : document.querySelector('results-list');
    const key = keyOf(location.href);
    if (!list || busy.has(list) || seen.has(key)) return;
    const frozen = !!document.startViewTransition && !dlg && !matchMedia('(prefers-reduced-motion: reduce)').matches;
    start(list, dlg ? 'i' : 'b', frozen);
    const end = () => (waits.delete(key), clearTimeout(t), stop(list));
    waits.set(key, end);
    const t = setTimeout(end, 15000);
    setTimeout(() => begun.has(key) || seen.has(key) || end(), 400);
  });
  const sectionDone = (key) => {
    seen.add(key);
    begun.delete(key);
    const w = waits.get(key);
    if (w) w();
  };

  const fetch0 = window.fetch;
  window.fetch = function (input, init) {
    const p = fetch0.apply(this, arguments);
    let job = null;
    let key = null;
    try {
      const url = String(input && input.url ? input.url : input);
      if (/[?&]section_id=/.test(url)) begun.add((key = keyOf(url)));
      job = pick(url);
    } catch (e) {}
    if (!job && !key) return p;
    let el, isAdd;
    if (job) {
      [el, , isAdd] = job;
      src = null;
      start(el, job[1]);
    }
    let done = false;
    const end = (ok) => {
      if (done) return;
      done = true;
      clearTimeout(guard);
      if (key) ok ? sectionDone(key) : begun.delete(key);
      if (!el) return;
      stop(el);
      if (isAdd && ok) added(el);
    };
    const guard = setTimeout(() => end(false), 15000);
    return p.then(
      (r) => {
        /* Keep the mark up until the body is read, not just the headers. */
        for (const m of ['text', 'json']) {
          const f = r[m];
          r[m] = () => f.call(r).finally(() => end(r.ok));
        }
        setTimeout(() => end(r.ok), 8000);
        return r;
      },
      (err) => {
        end(false);
        throw err;
      }
    );
  };
})();
